import {
  selectDirectParentIds,
  selectNodeChildren,
  selectNodeParent,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  selectIsForceExpanded,
  selectNodeIsVisible,
  selectVisibleIds,
} from '@store/reducers/tags/visibility'
import classNames from 'classnames'
import type React from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { colorSets, TREE_MATRIX } from '@/constants/constants'
import Node from '../Node/Node'
import { Dropzone } from './Dropzone'

type TreeBranchProps = {
  depth: number
  nodeId: string
  sectionId: string
  index: number
  isFirst?: boolean
  isLast?: boolean
  isChild?: boolean
  isOne?: boolean
  isFooter?: boolean
}

const TreeBranch: React.FC<TreeBranchProps> = ({
  depth,
  nodeId,
  sectionId,
  index,
  isFirst,
  isLast,
  isChild,
  isOne,
  isFooter,
}) => {
  const treeMode = useSelector(migrationProjectSelector.getProjectTreeType)

  const node = useSelector(migrationProjectSelector.getProjectNodeById(nodeId))

  const directParents = useSelector(
    selectDirectParentIds([nodeId]),
    shallowEqual
  )

  const childIds = useSelector(selectNodeChildren(nodeId), shallowEqual)

  // Page-tag filter (OCT-5007): only the children that lead to a match are
  // rendered, and the connector-line flags below must be derived from those —
  // deriving them from `childIds` draws a "last child" line for a child that
  // is not on the artboard.
  const visibleChildIds = useSelector(selectVisibleIds(childIds), shallowEqual)

  const isVisible = useSelector(selectNodeIsVisible(nodeId))

  // holds a match while collapsed in the project — expanded for the duration of
  // the filter without writing the `collapsed` flag
  const isForceExpanded = useSelector(selectIsForceExpanded(nodeId))

  const nodeParent = useSelector(selectNodeParent(nodeId), shallowEqual)[0]

  const parentColorHex = useSelector(
    migrationProjectSelector.getProjectNodeColorHex(nodeParent)
  )

  const theme = useSelector(migrationProjectSelector.getProjectTheme)

  if (!node || !isVisible) {
    return null
  }

  const { variant, collapsed } = node
  const isCollapsed = collapsed && !isForceExpanded

  const dropzoneNode = {
    group: sectionId,
    parentId: directParents[0] || '',
    childIndex: index,
  }

  const treeColor =
    colorSets[parentColorHex.toUpperCase()]?.[theme] || parentColorHex

  return (
    <div
      className={classNames([
        'Tree-branch',
        `is-${variant}`,
        {
          'is-first': isFirst,
          'is-one': isOne,
          'is-last': isLast,
          'is-child': isChild,
          'is-collapsed': isCollapsed,
          'is-matrix': treeMode === TREE_MATRIX,
        },
      ])}
      style={{
        // @TODO refactor
        // @ts-expect-error
        '--tree-color': treeColor,
      }}
    >
      {/* @TODO support isFirst and isLast to be true for single node */}
      {(isFirst || isOne) && (
        <Dropzone type="before" node={{ ...dropzoneNode, childIndex: 0 }} />
      )}

      {/* @TODO review this and fix */}
      {/* @ts-ignore - Type is not assignable to type 'IntrinsicAttributes & Props' */}
      <Node {...node} />

      <Dropzone
        type="after"
        node={{
          ...dropzoneNode,
          childIndex: index + 1,
        }}
      />

      {visibleChildIds.length > 0 && !isCollapsed && (
        <div
          className={classNames('Tree-children', {
            'is-row': depth === 0,
            'is-footer': isFooter,
          })}
        >
          {/* // @TODO refactor */}
          {visibleChildIds.map((childId: string, i: number) => {
            const isOne = visibleChildIds.length === 1
            const isLast = !isOne && i === visibleChildIds.length - 1
            const isFirst = !isOne && i === 0
            const isChild = !isOne && !isFirst && !isLast

            return (
              <TreeBranch
                depth={depth + 1}
                key={childId}
                nodeId={childId}
                sectionId={sectionId}
                // position among ALL children, not just the visible ones: this
                // feeds the drop zones, and a filtered index would insert a
                // dragged page at the wrong place (OCT-5007)
                index={childIds.indexOf(childId)}
                isFirst={isFirst}
                isOne={isOne}
                isLast={isLast}
                isChild={isChild}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TreeBranch
